import React, { FC, useEffect, useRef, useState } from "react";
import Glide from "@glidejs/glide";
import { useAppContext } from "AppContext";
import { toFilterDateString, parseFilterDate } from "utils/filterDates";
import { useWishlist } from "WishlistContext";
import bedIcon from "images/card/bed-icon.svg";
import bathIcon from "images/card/bath-icon.svg";
import kitchenIcon from "images/card/kitchen-icon.svg";
import sleepsIcon from "images/card/sleeps-icon.svg";
import pinIcon from "images/card/pin-icon.svg";
import heartFilledIcon from "images/card/heart-filled-icon.svg";
import euroIcon from "images/card/euro-icon.svg";
import chevronLeft from "images/card/chevron-left.svg";
import chevronRight from "images/card/chevron-right.svg";
import getImageVariantUrl from "utils/getImageVariantUrl";
import { useLocale } from "i18n/locale";
import { formatAmount } from "utils/formatAmount";

export interface PropertyCardEuropeaProps {
  className?: string;
  data?: any;
  id?: string;
}

const MAX_IMAGES = 3;

const PropertyCardEuropea: FC<PropertyCardEuropeaProps> = ({
  className = "",
  data,
  id,
}) => {
  const { t, locale } = useLocale();
  const { filterState } = useAppContext();
  const { isSaved, toggleWishlist } = useWishlist();
  const glideRef = useRef<Glide | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const propertyId = data?._id ? String(data._id) : id ? String(id) : "";
  const liked = isSaved(propertyId);

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(propertyId);
  };

  const galleryImgs = data?.galleryImgs;
  const images = (galleryImgs || []).slice(0, MAX_IMAGES);
  const GLIDE_CLASS = `cardGlide_${id}`;

  useEffect(() => {
    if (!data || images.length === 0) return;

    const timeout = setTimeout(() => {
      const el = document.querySelector(`.${GLIDE_CLASS}`);
      if (!el) return;

      const glide = new Glide(`.${GLIDE_CLASS}`, {
        perView: 1,
        gap: 0,
        keyboard: false,
      });

      glide.on("run", () => {
        setActiveIndex(glide.index);
      });

      glide.mount();
      glideRef.current = glide;
    }, 10);

    return () => {
      clearTimeout(timeout);
      if (glideRef.current) {
        glideRef.current.destroy();
        glideRef.current = null;
      }
    };
  }, [GLIDE_CLASS, images.length]);

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const original = img.dataset.original || "";
    // Prefer the _compress variant (full resolution, lighter JPEG); if it is
    // missing, fall back to the full original.
    if (original && img.src !== original) {
      img.src = original;
    }
  };

  if (!data) return null;

  const {
    title,
    base_price,
    avgRatePerDay,
    bedrooms,
    bathrooms,
    toilets,
    sleeps,
    location,
    city,
    type_of_unit,
    status_of_unit,
  } = data;

  const price = avgRatePerDay ? Math.round(avgRatePerDay) : base_price;
  const displayLocation = location || city || "";
  const badge = type_of_unit?.replace(/-/g, " ") || status_of_unit?.replace(/-/g, " ") || "";

  // Interpolating filterState directly used to put "null" — or a whole
  // "Wed Aug 05 2026 ... GMT+0530" Date toString — into the query string.
  const startParam = toFilterDateString(filterState?.checkInDate) ?? "";
  const endParam = toFilterDateString(filterState?.checkOutDate) ?? "";
  const detailUrl = `/listing-stay-detail?id=${id}&start_date=${startParam}&number_of_guests=${filterState?.guestNumber}&end_date=${endParam}&number_of_children=${filterState?.number_of_children}&number_of_infants=${filterState?.number_of_infants}`;

  // Only shown once the guest has picked real dates — before that there's no
  // period to total, so we don't want a misleading single-night figure.
  const checkIn = parseFilterDate(filterState?.checkInDate);
  const checkOut = parseFilterDate(filterState?.checkOutDate);
  const nights =
    checkIn && checkOut
      ? Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
  // Total is derived from the UNROUNDED all-in nightly rate (avgRatePerDay
  // already bakes in rent, long-stay discount and fees), so it equals the
  // detail page's total. Rounding `price` (the whole-euro nightly figure)
  // first and then multiplying drifted the total by a few euros. Round to
  // cents only to clear floating-point noise from the division.
  const nightlyAllIn = avgRatePerDay ?? base_price;
  const total =
    nights > 0 && nightlyAllIn
      ? Math.round(Number(nightlyAllIn) * nights * 100) / 100
      : null;
  // Match the detail page's formatting: locale-aware separators (comma decimal
  // in French), cents only when the total isn't whole.
  const totalDisplay =
    total != null
      ? formatAmount(total, locale, Number.isInteger(total) ? 0 : 2)
      : null;

  const handleCardClick = () => {
    window.open(detailUrl, "_blank");
  };

  const handleBookNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(detailUrl, "_blank");
  };

  return (
    <div
      className={`group cursor-pointer ${className}`}
      onClick={handleCardClick}
    >
      {/* Image section */}
      <div className="relative w-full rounded-[12px] overflow-hidden">
        <div className={`${GLIDE_CLASS} relative overflow-hidden`}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">
              {images.map((img: string, i: number) => (
                <li key={i} className="glide__slide">
                  <div className="aspect-w-3 aspect-h-2">
                    <img
                      src={img ? getImageVariantUrl(img, "_compress") : "/sly_0470-sly_0472.jpg"}
                      data-original={img}
                      alt={title || ""}
                      className="object-cover w-full h-full"
                      loading="lazy"
                      onError={handleImgError}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Badge — top left */}
        {badge && (
          <div
            className="absolute top-[12px] left-[12px] bg-white rounded-[20px] px-[10px] flex items-center justify-center h-[23px] z-10"
            style={{ boxShadow: "0px 0px 1px rgba(0,0,0,0.25)" }}
          >
            <span
              className="text-[9px] leading-[24px] text-[#01152E] whitespace-nowrap capitalize"
              style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              {badge}
            </span>
          </div>
        )}

        {/* Heart — top right */}
        <button
          type="button"
          onClick={handleWishlistClick}
          title={liked ? "Remove from wishlist" : "Save to wishlist"}
          className="absolute top-[12px] right-[12px] z-10 flex items-center justify-center"
        >
          {liked ? (
            <img src={heartFilledIcon} alt="Saved" width={23} height={20} />
          ) : (
            <svg
              width={23}
              height={20}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth={2}
              style={{ filter: "drop-shadow(0px 0px 2px rgba(0,0,0,0.35))" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          )}
        </button>

        {/* Carousel controls — bottom right pill */}
        {images.length > 1 && (
          <div
            className="absolute bottom-[22px] right-[20px] z-10 flex items-center justify-center w-[80px] h-[24px] rounded-[12px]"
            style={{ backgroundColor: "rgba(42, 54, 84, 0.4)" }}
          >
            <div className="flex items-center gap-[2px]">
              {/* Left arrow */}
              <button
                className="flex items-center justify-center w-[18px] h-[24px]"
                onClick={(e) => {
                  e.stopPropagation();
                  glideRef.current?.go("<");
                }}
              >
                <img src={chevronLeft} alt="" className="w-[5px] h-[8px]" />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-[5px]">
                {images.map((_: string, i: number) => (
                  <div
                    key={i}
                    className="rounded-full bg-white"
                    style={{
                      width: activeIndex === i ? 8 : 6,
                      height: activeIndex === i ? 8 : 6,
                      opacity: activeIndex === i ? 1 : 0.6,
                    }}
                  />
                ))}
              </div>

              {/* Right arrow */}
              <button
                className="flex items-center justify-center w-[18px] h-[24px]"
                onClick={(e) => {
                  e.stopPropagation();
                  glideRef.current?.go(">");
                }}
              >
                <img src={chevronRight} alt="" className="w-[5px] h-[8px]" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content section */}
      <div className="flex flex-col mt-[16px]">
        {/* Title + Location */}
        <div className="flex flex-col">
          {/* Title + Price row */}
          <div className="flex items-center gap-[10px]">
            <div className="flex-1 min-w-0">
              <p
                className="text-[18px] leading-[24px] text-[#01152E] whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                {title}
              </p>
            </div>
            <div className="flex items-center gap-[2px] flex-shrink-0">
              <div className="flex items-center gap-[2px]">
                <img src={euroIcon} alt="€" className="w-[10px] h-[12px]" />
                <span
                  className="text-[16px] leading-[24px] text-[#01152E] whitespace-nowrap"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                >
                  {price}
                </span>
              </div>
              <span
                className="text-[12px] leading-[24px] text-[#01152E] whitespace-nowrap"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
              >
                {t("listingStay.perNight")}
              </span>
            </div>
          </div>

          {/* Location + Total row */}
          <div className="flex items-center gap-[10px]">
            <div className="flex-1 min-w-0 flex items-center gap-[6px]">
              <img src={pinIcon} alt="" className="w-[8px] h-[10px] flex-shrink-0" />
              <span
                className="text-[12px] leading-[24px] text-[#01152E]"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
              >
                {displayLocation}
              </span>
            </div>
            {total != null && (
              <span
                className="text-[12px] leading-[24px] text-[#01152E] whitespace-nowrap flex-shrink-0"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
              >
                &euro; {totalDisplay} {t("listingStay.total")}
              </span>
            )}
          </div>
        </div>

        {/* Amenities row + Book Now */}
        <div className="flex items-center justify-between gap-[12px] mt-[4px]">
          <div className="flex items-center gap-[24px]">
            {bedrooms != null && (
              <div className="flex items-center gap-[6px]">
                <img src={bedIcon} alt="" className="w-[19px] h-[14px]" />
                <span
                  className="text-[12px] leading-[24px] text-[#445162]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                >
                  {bedrooms}
                </span>
              </div>
            )}
            {bathrooms != null && (
              <div className="flex items-center gap-[6px]">
                <img src={bathIcon} alt="" className="w-[14px] h-[14px]" />
                <span
                  className="text-[12px] leading-[24px] text-[#445162]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                >
                  {bathrooms}
                </span>
              </div>
            )}
            {toilets != null && (
              <div className="flex items-center gap-[6px]">
                <img src={kitchenIcon} alt="" className="w-[12px] h-[14px]" />
                <span
                  className="text-[12px] leading-[24px] text-[#445162]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                >
                  {toilets}
                </span>
              </div>
            )}
            {sleeps != null && (
              <div className="flex items-center gap-[6px]">
                <img src={sleepsIcon} alt="" className="w-[18px] h-[14px]" />
                <span
                  className="text-[12px] leading-[24px] text-[#445162]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                >
                  {sleeps}
                </span>
              </div>
            )}
          </div>

          {total != null && (
            <button
              type="button"
              onClick={handleBookNow}
              className="flex items-center justify-center h-[30px] px-[24px] rounded-[30px] flex-shrink-0 cursor-pointer border-0 hover:shadow-sm transition-shadow"
              style={{ backgroundColor: "#13263F" }}
            >
              <span
                className="text-[12px] leading-[24px] text-white whitespace-nowrap"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                {t("common.bookNow")}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCardEuropea;
